import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { MerchantSettingsOverlay } from '../settings/routes.js';

import { useLanguage } from '../../i18n/language.js';
import { workspaceCopy } from '../../merchant/copy.js';
import { ToastHost } from '../../merchant/ui/primitives.js';
import { MerchantMobileHeader } from './MerchantMobileHeader.js';
import { MerchantSidebar } from './MerchantSidebar.js';
import { MerchantWorkspaceShellProvider } from '../../merchant/shell-context.js';

/**
 * The authenticated merchant frame: the fixed sidebar (a drawer under
 * 1024px), the compact bar, the shared workspace snapshot, and the toast.
 * Pages render into the content column; nothing else draws navigation.
 */
export function MerchantWorkspaceShell({
  children,
}: {
  readonly children?: ReactNode;
}): ReactNode {
  return (
    <ToastHost>
      <MerchantWorkspaceShellProvider>
        <MerchantWorkspaceShellFrame>{children}</MerchantWorkspaceShellFrame>
      </MerchantWorkspaceShellProvider>
    </ToastHost>
  );
}

function MerchantWorkspaceShellFrame({
  children,
}: {
  readonly children?: ReactNode;
}): ReactNode {
  const { language } = useLanguage();
  const copy = workspaceCopy(language);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const settingsTab = location.pathname.startsWith('/settings')
    ? null
    : params.get('settings');
  const usesWireframe =
    !location.pathname.startsWith('/business-profile') &&
    !location.pathname.startsWith('/merchant/intakes');
  const menuTrigger = useRef<HTMLButtonElement | null>(null);
  const sidebar = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const onBreakpointChange = () => {
      if (desktop.matches) setMobileOpen(false);
    };
    desktop.addEventListener('change', onBreakpointChange);
    return () => {
      desktop.removeEventListener('change', onBreakpointChange);
    };
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusableControls = () =>
      Array.from(
        sidebar.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]),[href],[tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((control) => control.getClientRects().length > 0);
    focusableControls()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // The topmost menu owns the first Escape; a second closes the drawer.
        if (sidebar.current?.querySelector('[role="menu"]')) return;
        event.preventDefault();
        setMobileOpen(false);
        return;
      }
      const controls = focusableControls();
      if (event.key !== 'Tab' || controls.length === 0) return;
      const first = controls[0];
      const last = controls.at(-1);
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      menuTrigger.current?.focus();
    };
  }, [mobileOpen]);

  return (
    <div className={`mj-merchant-workspace${collapsed ? ' is-sidebar-collapsed' : ''}`}>
      <a className="mj-skip-link" href="#merchant-main-content">
        {copy.nav.skip}
      </a>
      <MerchantSidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onCollapsedChange={setCollapsed}
        onMobileClose={() => {
          setMobileOpen(false);
        }}
        sidebarRef={sidebar}
      />
      <MerchantMobileHeader
        mobileOpen={mobileOpen}
        onMenuOpen={() => {
          setMobileOpen(true);
        }}
        triggerRef={menuTrigger}
      />
      <main
        id="merchant-main-content"
        className="mj-merchant-workspace-content"
        data-workspace-design={usesWireframe ? 'v1.12' : undefined}
        tabIndex={-1}
        inert={mobileOpen}
      >
        {usesWireframe ? (
          <div className="mj-merchant-atmosphere" aria-hidden="true">
            <i />
            <i />
            <i />
          </div>
        ) : null}
        <div inert={settingsTab !== null}>{children ?? <Outlet />}</div>
        {settingsTab !== null && (
          <MerchantSettingsOverlay
            overlayTab={settingsTab}
            onClose={() => {
              const next = new URLSearchParams(params);
              next.delete('settings');
              setParams(next, { replace: true });
            }}
          />
        )}
      </main>
    </div>
  );
}
